<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import messages from 'dashboard/components-next/Contacts/Portfolio/messages';
import { useRoute, useRouter } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import PortfolioView from 'dashboard/components-next/Contacts/Portfolio/PortfolioView.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n({ useScope: 'local', messages });
const labels = useMapGetter('labels/getLabels');
const createDialog = ref(null);
const labelDialog = ref(null);
const portfolioDialog = ref(null);
const labelTarget = ref(null);
const portfolioTarget = ref(null);
const chosenLabels = ref([]);
const actionLoading = ref(false);
const portfolioLoading = ref(false);
const portfolioForm = ref({ state: 'unclassified', owner_id: '' });
const selectedIds = ref([]);
const filters = ref({ owner_id: '', state: '', label: '' });
const sort = ref('name');
const direction = ref('asc');
const contacts = ref([]);
const selected = ref(null);
const meta = ref({});
const scope = ref('mine');
const query = ref('');
const page = ref(1);
const loading = ref(false);
const error = ref(false);
const conversations = ref([]);
const historyLoading = ref(false);
const historyError = ref(false);
let requestId = 0;
let historyId = 0;
let searchTimer;
let initialized = false;

const select = async contact => {
  selected.value = contact;
  conversations.value = [];
  historyError.value = false;
  historyId += 1;
  const id = historyId;
  if (!contact) {
    historyLoading.value = false;
    return;
  }
  historyLoading.value = true;
  try {
    const { data } = await ContactAPI.getConversations(contact.id);
    if (id === historyId) conversations.value = data.payload || [];
  } catch {
    if (id === historyId) historyError.value = true;
  } finally {
    if (id === historyId) historyLoading.value = false;
  }
};
const fetchContacts = async () => {
  requestId += 1;
  const id = requestId;
  loading.value = true;
  error.value = false;
  selectedIds.value = [];
  select(null);
  try {
    const { data } = await ContactAPI.portfolio({
      scope: scope.value,
      q: query.value,
      page: page.value,
      ...filters.value,
      sort: sort.value,
      direction: direction.value,
    });
    if (id !== requestId) return;
    meta.value = data.meta;
    if (!initialized && data.meta.administrative) {
      initialized = true;
      scope.value = 'all';
      fetchContacts();
      return;
    }
    initialized = true;
    contacts.value = data.payload;
    if (scope.value !== 'all') select(data.payload[0] || null);
  } catch {
    if (id === requestId) {
      contacts.value = [];
      meta.value = { ...meta.value, count: 0, page: 1 };
      error.value = true;
    }
  } finally {
    if (id === requestId) loading.value = false;
  }
};
const changeScope = value => {
  clearTimeout(searchTimer);
  scope.value = value;
  page.value = 1;
  query.value = '';
  filters.value = { owner_id: '', state: '', label: '' };
  fetchContacts();
};
const search = value => {
  query.value = value;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    fetchContacts();
  }, 300);
};
const changePage = value => {
  page.value = value;
  fetchContacts();
};
const changeFilters = value => {
  clearTimeout(searchTimer);
  filters.value = value;
  page.value = 1;
  fetchContacts();
};
const changeSort = value => {
  direction.value =
    sort.value === value && direction.value === 'asc' ? 'desc' : 'asc';
  sort.value = value;
  page.value = 1;
  fetchContacts();
};
const toggleSelection = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(value => value !== id)
    : [...selectedIds.value, id];
};
const openLabels = contact => {
  labelTarget.value = contact || null;
  chosenLabels.value = [...(contact?.labels || [])];
  labelDialog.value.open();
};
const openPortfolio = contact => {
  portfolioTarget.value = contact;
  portfolioForm.value = {
    state: contact.portfolio?.state || 'unclassified',
    owner_id: contact.portfolio?.owner_id
      ? String(contact.portfolio.owner_id)
      : '',
  };
  portfolioDialog.value.open();
};
const savePortfolio = async () => {
  if (portfolioLoading.value) return;
  const state = portfolioForm.value.state;
  const ownerId = portfolioForm.value.owner_id;
  if (state === 'owned' && !ownerId) {
    useAlert(t('portfolioOwnerRequired'));
    return;
  }
  portfolioLoading.value = true;
  try {
    await ContactAPI.updatePortfolio(portfolioTarget.value.id, {
      state,
      owner_id: state === 'owned' ? Number(ownerId) : null,
    });
    portfolioDialog.value.close();
    useAlert(t('portfolioSaved'));
    await fetchContacts();
  } catch {
    useAlert(t('portfolioError'));
  } finally {
    portfolioLoading.value = false;
  }
};
const saveLabels = async () => {
  actionLoading.value = true;
  try {
    if (labelTarget.value) {
      await ContactAPI.updateContactLabels(
        labelTarget.value.id,
        chosenLabels.value
      );
    } else {
      const results = await Promise.allSettled(
        selectedIds.value.map(async id => {
          const { data } = await ContactAPI.getContactLabels(id);
          const updatedLabels = [
            ...new Set([...(data.payload || []), ...chosenLabels.value]),
          ];
          await ContactAPI.updateContactLabels(id, updatedLabels);
        })
      );
      if (results.some(result => result.status === 'rejected')) {
        labelDialog.value.close();
        useAlert(t('partialError'));
        await fetchContacts();
        return;
      }
    }
    labelDialog.value.close();
    useAlert(t('labelsSaved'));
    await fetchContacts();
  } catch {
    useAlert(t('actionError'));
  } finally {
    actionLoading.value = false;
  }
};
const createContact = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createDialog.value.onSuccess();
    useAlert(t('created'));
    changeScope(meta.value.administrative ? 'all' : 'mine');
  } catch {
    useAlert(t('actionError'));
  }
};
const open = contact =>
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId: contact.id },
  });
const openConversation = conversation =>
  router.push(
    `/app/accounts/${route.params.accountId}/conversations/${conversation.id}`
  );
watch(
  () => route.params.accountId,
  () => {
    initialized = false;
    scope.value = 'mine';
    query.value = '';
    page.value = 1;
    meta.value = {};
    filters.value = { owner_id: '', state: '', label: '' };
    fetchContacts();
  },
  { immediate: true }
);
const refreshPortfolio = event => {
  if (
    !event.detail?.account_id ||
    Number(event.detail.account_id) === Number(route.params.accountId)
  )
    fetchContacts();
};
onMounted(() => {
  window.addEventListener('contacts:portfolio-changed', refreshPortfolio);
  store.dispatch('labels/get');
});
onBeforeUnmount(() => {
  window.removeEventListener('contacts:portfolio-changed', refreshPortfolio);
  clearTimeout(searchTimer);
  requestId += 1;
  historyId += 1;
});
</script>

<template>
  <PortfolioView
    :contacts="contacts"
    :selected="selected"
    :meta="meta"
    :scope="scope"
    :query="query"
    :loading="loading"
    :error="error"
    :conversations="conversations"
    :history-loading="historyLoading"
    :history-error="historyError"
    :filters="filters"
    :sort="sort"
    :direction="direction"
    :labels="labels"
    :selected-ids="selectedIds"
    @filters="changeFilters"
    @sort="changeSort"
    @toggle="toggleSelection"
    @toggle-all="
      selectedIds = $event ? contacts.map(contact => contact.id) : []
    "
    @clear-selection="selectedIds = []"
    @labels="openLabels"
    @portfolio="openPortfolio"
    @create="createDialog.dialogRef.open()"
    @scope="changeScope"
    @search="search"
    @select="select"
    @page="changePage"
    @retry="fetchContacts"
    @open="open"
    @conversation="openConversation"
  />
  <CreateNewContactDialog
    ref="createDialog"
    :aria-label="
      $t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT')
    "
    form-class="[&_input]:placeholder:!text-n-slate-11 dark:[&_input]:placeholder:!text-n-slate-11"
    submit-button-class="!bg-[#1d6fd8] hover:enabled:!bg-[#195fba] hover:enabled:!brightness-100 focus-visible:!brightness-100 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
    @create="createContact"
  />
  <Dialog
    ref="labelDialog"
    :aria-label="labelTarget ? t('editLabels') : t('addLabels')"
    :title="labelTarget ? t('editLabels') : t('addLabels')"
    :description="
      labelTarget?.name || t('selectedCount', { count: selectedIds.length })
    "
    :confirm-button-label="t('save')"
    confirm-button-class="!bg-[#1d6fd8] hover:enabled:!bg-[#195fba] hover:enabled:!brightness-100 focus-visible:!brightness-100 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
    :is-loading="actionLoading"
    :disable-confirm-button="
      actionLoading || (!labelTarget && !chosenLabels.length)
    "
    @confirm="saveLabels"
  >
    <div class="max-h-80 overflow-auto space-y-2">
      <label
        v-for="label in labels"
        :key="label.id"
        class="flex items-center gap-3 p-2 rounded hover:bg-n-alpha-1"
      >
        <input
          v-model="chosenLabels"
          type="checkbox"
          :value="label.title"
          class="!m-0"
        />
        <span class="text-sm">{{ label.title }}</span>
      </label>
      <p v-if="!labels.length" class="text-sm text-n-slate-11">
        {{ t('noLabels') }}
      </p>
    </div>
  </Dialog>
  <Dialog
    ref="portfolioDialog"
    :aria-label="t('editPortfolio')"
    :title="t('editPortfolio')"
    :description="portfolioTarget?.name || ''"
    :confirm-button-label="t('save')"
    :is-loading="portfolioLoading"
    :disable-confirm-button="portfolioLoading"
    @confirm="savePortfolio"
  >
    <div class="flex flex-col gap-4">
      <label class="text-sm text-n-slate-11">
        {{ t('portfolioState') }}
        <select v-model="portfolioForm.state" class="mt-1 !mb-0 w-full">
          <option
            v-if="portfolioTarget?.portfolio?.expires_at"
            value="owned"
          >
            {{ t('owned') }}
          </option>
          <option value="available">{{ t('availableState') }}</option>
          <option value="unclassified">{{ t('unclassified') }}</option>
        </select>
      </label>
      <label
        v-if="portfolioForm.state === 'owned'"
        class="text-sm text-n-slate-11"
      >
        {{ t('owner') }}
        <select v-model="portfolioForm.owner_id" class="mt-1 !mb-0 w-full">
          <option value="">{{ t('selectOwner') }}</option>
          <option
            v-for="owner in meta.owners || []"
            :key="owner.id"
            :value="String(owner.id)"
          >
            {{ owner.name }}
          </option>
        </select>
      </label>
      <p class="m-0 text-xs leading-5 text-n-slate-11">
        {{ t('portfolioEditHelp') }}
      </p>
    </div>
  </Dialog>
</template>
